import {
    DefaultProps,
    PickingInfo,
    Position,
    CompositeLayer,
    CompositeLayerProps,
} from "@deck.gl/core"
import { HexagonLayer } from "@deck.gl/aggregation-layers"
import { get, set } from "nodash"
import HotspotPointsLayer from "./HotspotPointsLayer.class"
import HotspotHeatmapLayer from "./HotspotHeatmapLayer.class"
import { ShowLayerByIdMap } from "features/map/layers/types"
import { isMapToolActive } from "features/map/layers/pickingGuard"
import store from "integrations/redux/store"
import { hotspotHexagonColorRange, hotspotHexagonRadius } from "./constants"
import HotspotPolygonLayer from "./HotspotPolygonLayer.class"
import {
    AnyAction,
    ColorSwatchCategory,
    hexColorSwatches,
    hotspotPointFillOpacity,
    hotspotPointFillOpacity8Bit,
    HotspotPointTypes,
    HotspotProperties,
    hotspotSizes,
    HotspotWithDistance,
    HotspotWithLabel,
} from "@nova/constants"
import { convertHexStringToRGBArray } from "@nova/util-strings"
import { decodeNumberToColor } from "@nova/serializer/src/client/util"
import { InfraredFlight, Polygon } from "@nova/protobuf"
import { setHotspotPanelState } from "panels/hotspot/slice"
import {
    NUMBER_OF_BARS,
    sizeBinByHotspotId,
    thermalBinByHotspotId,
} from "features/map/inspector-panel/hotspots/filters/module"
import { getAncestors } from "panels/content/side-panel/utils"
import { selectAllEntitiesMap } from "panels/content/side-panel/slice"
import { DataFilterExtension } from "@deck.gl/extensions"
import { getGlobalTerrainData } from "features/map/GlobalTerrainManager"

const DEFAULT_HOTSPOT_POINT_SIZE = 6

export type HotspotsLayerProps = CompositeLayerProps & _HotspotsLayerProps

type _HotspotsLayerProps = {
    flights?: Record<string, InfraredFlight>
    showLayer?: {
        points: ShowLayerByIdMap
        labels: ShowLayerByIdMap
        polygons: ShowLayerByIdMap
    }
    previewLayer?: {
        points: ShowLayerByIdMap
        polygons: ShowLayerByIdMap
    }
    pointId?: string
    labelId?: string
    featureData?: { hotspots: boolean; polygons: boolean }
    hotspotProperties?: Record<string, HotspotProperties>
    perimeterData?: Polygon[]
    pdfSnapshotOpen?: boolean
    flight?: InfraredFlight
    activeHotspotPanelHotspotId?: string
    selectedHotspotsMap?: Record<string, HotspotWithLabel>
}

const hotspotEntityPositionExtractor = (d: HotspotWithDistance) => {
    return [d.longitude, d.latitude] as Position
}

const defaultProps: DefaultProps<HotspotsLayerProps> = {
    flights: {},
    showLayer: {
        points: {},
        labels: {},
        polygons: {},
    },
    previewLayer: {
        points: {},
        polygons: {},
    },
    hotspotProperties: {},
}
const hotspotLayerFilter =
    (
        filter: Record<string, boolean>,
        flights: Record<string, InfraredFlight>,
        types: HotspotPointTypes[] | "*"
    ) =>
    (d: HotspotWithDistance) => {
        const pointType = flights[d.flightId]?.pointType || HotspotPointTypes.Point
        if (types === "*") return +filter[d.flightId]
        return +(filter[d.flightId] && types.includes(pointType))
    }

// TODO: This might need to use the hook so it picks up changes to ancestor visibility
const isAnyHeatVisible = (flights: Record<string, InfraredFlight>) => {
    const state = store.getState()
    const entityMap = selectAllEntitiesMap(state)
    return Object.values(flights).some(
        (flight) =>
            flight.visible &&
            !getAncestors(flight.id, entityMap).find((ancestor) => !ancestor.visible) &&
            flight.pointType === HotspotPointTypes.Heat
    )
}

const blue500 = [39, 187, 255, 255] as [number, number, number, number]
/**
 * a composite class to render the points and labels for a set of hotspots
 */
export default class HotspotsLayer<
    ExtraPropsType extends CompositeLayerProps = HotspotsLayerProps,
> extends CompositeLayer<ExtraPropsType & Required<_HotspotsLayerProps>> {
    static defaultProps = defaultProps
    static layerName = "HotspotsLayer"
    state = {} as { subLayerData: []; flights: Record<string, InfraredFlight> }
    updateState({
        changeFlags,
    }: {
        props: HotspotsLayerProps
        changeFlags: Record<string, unknown>
    }) {
        if (changeFlags.dataChanged) {
            const data = get(this.props, "data")
            this.setState({ subLayerData: data, flights: this.props.flights })
        } else if (changeFlags.propsChanged) {
            this.setState({ flights: this.props.flights })
        }
    }
    renderLayers() {
        const layers = [
            new HotspotPolygonLayer({
                id: "polygon-layer",
                data: this.state.subLayerData,
                visible: true,
                extensions: [
                    new DataFilterExtension({ filterSize: 1 }),
                    ...getGlobalTerrainData().extensions,
                ],
                filterRange: [1, 1],
                updateTriggers: {
                    getPolygon: this.props.updateTriggers.getPosition,
                    getFilterValue: [this.props.showLayer.polygons],
                    getLineColor: [this.props.previewLayer.polygons, this.props.hotspotProperties],
                    getFillColor: this.props.hotspotProperties,
                },
                getPolygon: (d) => {
                    if (d.polygon)
                        return d.polygon.map((p) => [p.longitude, p.latitude]) as Position[]
                    else return []
                },
                getFillColor: (d: AnyAction) =>
                    new Uint8Array(
                        (() => {
                            const opacity = this.props.previewLayer.polygons[d.flightId as string]
                                ? 125
                                : 255
                            const hexColor = this.props.flights[d.flightId as string]?.polyFill
                            const color = hexColor
                                ? convertHexStringToRGBArray(decodeNumberToColor(hexColor))
                                : undefined
                            if (!color) return [164, 84, 70, opacity]
                            if (color.length === 4) return color
                            return [...color, opacity]
                        })()
                    ),
                getLineColor: (d: AnyAction) =>
                    new Uint8Array(
                        (() => {
                            const opacity = this.props.previewLayer.polygons[d.flightId as string]
                                ? 125
                                : 255
                            const hexColor = this.props.flights[d.flightId as string]?.polyStroke
                            const color = hexColor
                                ? convertHexStringToRGBArray(decodeNumberToColor(hexColor))
                                : [194, 96, 76, opacity]
                            if (!color) return [194, 96, 76, opacity]
                            if (color.length === 4) return color
                            return [...color, opacity]
                        })()
                    ),
                getFilterValue: hotspotLayerFilter(
                    this.props.showLayer.polygons,
                    this.props.flights,
                    "*"
                ),
            }),
            // The heatmap layer enters an irrecoverable state if terrain is loaded
            // TODO: This is a workaround, may require a rework of the HotspotHeatmapLayer
            ...(!getGlobalTerrainData().terrainState
                ? [
                      new HotspotHeatmapLayer({
                          data: this.state.subLayerData || [],
                          visible: isAnyHeatVisible(this.props.flights),
                          getPosition: hotspotEntityPositionExtractor,
                          updateTriggers: {
                              getPosition: this.props.updateTriggers.getPosition,
                              getWeight: [this.props.showLayer.points],
                          },
                          getWeight: hotspotLayerFilter(
                              this.props.showLayer.points,
                              this.props.flights,
                              [HotspotPointTypes.Heat]
                          ),
                      }),
                  ]
                : []),
            new HotspotPointsLayer({
                id: this.props.pointId,
                data: this.state.subLayerData,
                visible: true,
                pickable: this.props.pickable,
                extensions: [
                    new DataFilterExtension({ filterSize: 1 }),
                    ...getGlobalTerrainData().extensions,
                ],
                filterRange: [1, 1],
                updateTriggers: {
                    getPosition: [
                        this.props.updateTriggers.getPosition,
                        this.props.activeHotspotPanelHotspotId,
                    ],
                    getFilterValue: [
                        this.props.updateTriggers.pointsLayerFilter,
                        this.props.hotspotProperties,
                        this.props.perimeterData,
                        this.props.updateTriggers.getPosition,
                    ],
                    getFillColor: [
                        this.props.previewLayer.points,
                        this.props.hotspotProperties,
                        this.props.perimeterData,
                        this.props.selectedHotspotsMap,
                        this.props.activeHotspotPanelHotspotId,
                        this.props.flights,
                    ],
                    getRadius: [this.props.activeHotspotPanelHotspotId, this.props.flights],
                    getLineColor: [
                        this.props.activeHotspotPanelHotspotId,
                        this.props.selectedHotspotsMap,
                    ],
                    getLineWidth: this.props.activeHotspotPanelHotspotId,
                },
                getPosition: (d) => {
                    const newPosition = hotspotEntityPositionExtractor(d)

                    return [
                        newPosition[0],
                        newPosition[1],
                        d.id === this.props.activeHotspotPanelHotspotId ? 1 : 0,
                    ] as [number, number, number]
                },
                getLineColor: (d: HotspotWithDistance) => {
                    if (d.id === this.props.activeHotspotPanelHotspotId) {
                        return [0, 0, 0]
                    }

                    if (this.props.selectedHotspotsMap[d.id]) {
                        return blue500
                    }

                    return (
                        d.color ||
                        convertHexStringToRGBArray(hexColorSwatches[ColorSwatchCategory.Hotspot][0])
                    )
                },
                getLineWidth: (d: HotspotWithDistance) =>
                    d.id === this.props.activeHotspotPanelHotspotId ? 1.5 : 1,
                getFillColor: (d: HotspotWithDistance) => {
                    const color = [
                        ...(d.color ||
                            convertHexStringToRGBArray(
                                hexColorSwatches[ColorSwatchCategory.Hotspot][0]
                            )),
                    ]

                    if (!!this.props.flights[d.flightId].relativeSizeEnabled) {
                        if (color.length === 4) color[3] *= hotspotPointFillOpacity
                        else color.push(hotspotPointFillOpacity8Bit)
                    }

                    if (
                        this.props.selectedHotspotsMap[d.id] ||
                        this.props.activeHotspotPanelHotspotId === d.id
                    ) {
                        return blue500
                    }

                    return color as [number, number, number, number]
                },
                getRadius: (d: HotspotWithDistance) => {
                    let radius =
                        (!!this.props.flights[d.flightId].relativeSizeEnabled
                            ? hotspotSizes[
                                  d.sizeIndex !== undefined
                                      ? d.sizeIndex
                                      : // This next line is the sizeIndex for manually placed (or pre-v3.00) hotspots
                                        Math.floor(hotspotSizes.length / 2)
                              ]
                            : DEFAULT_HOTSPOT_POINT_SIZE) / 2

                    if (d.id === this.props.activeHotspotPanelHotspotId) {
                        radius += 2
                    }

                    return radius
                },
                getFilterValue: hotspotLayerFilter(
                    this.props.showLayer.points,
                    this.props.flights,
                    [HotspotPointTypes.Point]
                ),
            }),
            new HexagonLayer<HotspotWithDistance>({
                id: "hexagon-layer",
                data:
                    this.state.subLayerData && !this.props.pdfSnapshotOpen
                        ? this.state.subLayerData.filter(
                              hotspotLayerFilter(this.props.showLayer.points, this.props.flights, [
                                  HotspotPointTypes.Hex,
                              ])
                          )
                        : [],
                getPosition: hotspotEntityPositionExtractor,
                getColorWeight: (hotspot: HotspotWithDistance) => thermalBinByHotspotId[hotspot.id],
                getElevationWeight: (hotspot: HotspotWithDistance) => {
                    return (this.state.flights as Record<string, InfraredFlight>)[hotspot.flightId]
                        .hexIsFlat
                        ? 0
                        : sizeBinByHotspotId[hotspot.id]
                },
                extensions: [...getGlobalTerrainData().extensions],
                colorAggregation: "MAX",
                colorDomain: [0, NUMBER_OF_BARS - 1],
                colorRange: hotspotHexagonColorRange,
                elevationAggregation: "SUM",
                elevationDomain: [0, NUMBER_OF_BARS - 1],
                elevationRange: [0, 400],
                radius: hotspotHexagonRadius,
                material: {
                    ambient: 0.8,
                    diffuse: 0.2,
                    shininess: 0,
                    specularColor: [0, 0, 0],
                },
                extruded: true,
            }),
        ]

        return layers
    }

    onClick(info: PickingInfo, event: MouseEvent) {
        return handleHotspotClick(info, event)
    }
}

export const handleHotspotClick = (info: PickingInfo, event: MouseEvent) => {
    if (isMapToolActive()) return false

    set(event, "clickedHotspot", true)
    const dispatch = store.dispatch
    const id = get(info, "object.id") as string | undefined
    const imageId = get(info, "object.imageId") as string | undefined
    const flightId = get(info, "object.flightId") as string | undefined

    dispatch(
        setHotspotPanelState({
            hotspotId: id,
            imageId: imageId,
            flightId: flightId,
        })
    )

    return true
}
